// Phase 5 (web revival) — WebSocket subscription + cache invalidation.
//
// Lifecycle:
// - `connect()` opens a WS to /v1/events?token=<access>&since=<last_event_id>.
//   - `since` is read from idb-keyval so a tab restart picks up missed
//     events from the server's 24h ring buffer (Option B per CONTRACT
//     0.6.0: an unknown/aged-out since silently starts fresh, never
//     emits server.resync_required).
// - On each frame, the event_id is persisted before we hand the payload
//   to the invalidation handlers — that way if the handler throws, we
//   still don't re-process the same event next reconnect.
// - On close, exponential-backoff reconnect (1s, 2s, 4s, capped at 30s).
// - `disconnect()` is called from the logout flow; it suppresses the
//   reconnect timer.

import { get, set } from 'idb-keyval';
import { queryClient, qk } from './queryClient';
import {
  clearSession,
  getAccessToken,
  isAuthenticated,
  subscribe as subscribeAuth,
} from './auth';
import type { WSEvent } from '../api/types';

const SINCE_KEY = 'dvphoto:last_event_id';

let socket: WebSocket | null = null;
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let intentionalClose = false;

function backoffMs(): number {
  const ms = Math.min(30_000, 1000 * Math.pow(2, reconnectAttempts));
  reconnectAttempts += 1;
  return ms;
}

async function loadSince(): Promise<string | undefined> {
  const val = await get<string>(SINCE_KEY);
  return val ?? undefined;
}

async function saveSince(id: string): Promise<void> {
  await set(SINCE_KEY, id);
}

function buildURL(token: string, since?: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const host = window.location.host;
  const params = new URLSearchParams({ token });
  if (since) params.set('since', since);
  return `${proto}://${host}/v1/events?${params.toString()}`;
}

function scheduleReconnect() {
  if (intentionalClose) return;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  const delay = backoffMs();
  reconnectTimer = setTimeout(() => {
    void connect();
  }, delay);
}

export async function connect(): Promise<void> {
  if (!isAuthenticated()) return;
  if (socket && socket.readyState !== WebSocket.CLOSED) return;
  const token = getAccessToken();
  if (!token) return;

  intentionalClose = false;
  const since = await loadSince();
  const url = buildURL(token, since);

  const ws = new WebSocket(url);
  socket = ws;

  ws.addEventListener('open', () => {
    reconnectAttempts = 0;
  });

  ws.addEventListener('message', (evt) => {
    void handleFrame(evt.data);
  });

  ws.addEventListener('close', () => {
    socket = null;
    if (!intentionalClose) scheduleReconnect();
  });

  ws.addEventListener('error', () => {
    // 'close' will fire right after; let it handle reconnect.
  });
}

export function disconnect(): void {
  intentionalClose = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket && socket.readyState !== WebSocket.CLOSED) {
    socket.close(1000, 'client_disconnect');
  }
  socket = null;
  reconnectAttempts = 0;
}

async function handleFrame(raw: unknown): Promise<void> {
  if (typeof raw !== 'string') return;
  let event: WSEvent;
  try {
    event = JSON.parse(raw) as WSEvent;
  } catch {
    return;
  }
  if (event.event_id) {
    await saveSince(event.event_id);
  }
  routeEvent(event);
}

function pickString(payload: Record<string, unknown>, key: string): string | null {
  const v = payload[key];
  return typeof v === 'string' ? v : null;
}

function routeEvent(event: WSEvent): void {
  const t = event.type;
  const p = event.payload ?? {};

  if (t === 'server.logout') {
    // Stolen-token defense fired by the server. Drop session and let the
    // router redirect to /login.
    clearSession();
    disconnect();
    return;
  }

  if (t === 'server.resync_required') {
    // Should never happen under Option B replay semantics, but if it
    // does, blow away everything we know and re-fetch.
    queryClient.invalidateQueries();
    return;
  }

  // Asset list cache lives under ['assets', filter?]; invalidate the
  // namespace prefix so every filtered variant refetches.
  if (
    t === 'asset.created' ||
    t === 'asset.deleted' ||
    t === 'asset.restored'
  ) {
    queryClient.invalidateQueries({ queryKey: qk.assets() });
    queryClient.invalidateQueries({ queryKey: qk.feedSharedWithMe() });
    const assetID = pickString(p, 'asset_id');
    if (assetID) queryClient.invalidateQueries({ queryKey: qk.asset(assetID) });
    return;
  }

  if (t === 'asset.updated' || t === 'asset.baked') {
    queryClient.invalidateQueries({ queryKey: qk.assets() });
    const assetID = pickString(p, 'asset_id');
    if (assetID) {
      queryClient.invalidateQueries({ queryKey: qk.asset(assetID) });
      queryClient.invalidateQueries({ queryKey: qk.assetEdits(assetID) });
    }
    return;
  }

  if (t === 'asset.share.created' || t === 'asset.share.revoked') {
    const assetID = pickString(p, 'asset_id');
    if (assetID) {
      queryClient.invalidateQueries({ queryKey: qk.assetShares(assetID) });
      queryClient.invalidateQueries({ queryKey: qk.asset(assetID) });
    }
    queryClient.invalidateQueries({ queryKey: qk.feedSharedWithMe() });
    return;
  }

  if (t === 'comment.created' || t === 'comment.updated' || t === 'comment.deleted') {
    const assetID = pickString(p, 'asset_id');
    if (assetID) {
      queryClient.invalidateQueries({ queryKey: qk.assetComments(assetID) });
      queryClient.invalidateQueries({ queryKey: qk.asset(assetID) });
    }
    return;
  }

  if (t === 'reaction.added' || t === 'reaction.removed') {
    const assetID = pickString(p, 'asset_id');
    if (assetID) {
      queryClient.invalidateQueries({ queryKey: qk.assetReactions(assetID) });
      queryClient.invalidateQueries({ queryKey: qk.asset(assetID) });
    }
    return;
  }

  if (t === 'album.created' || t === 'album.updated' || t === 'album.deleted') {
    queryClient.invalidateQueries({ queryKey: qk.albums() });
    const albumID = pickString(p, 'album_id');
    if (albumID) {
      queryClient.invalidateQueries({ queryKey: qk.album(albumID) });
      queryClient.invalidateQueries({ queryKey: qk.albumAssets(albumID) });
    }
    return;
  }
}

// Wire (dis)connect to auth state. The router calls connect() once at
// mount; the auth subscriber here covers logout → reconnect-after-login.
subscribeAuth(() => {
  if (isAuthenticated()) {
    void connect();
  } else {
    disconnect();
  }
});
